"use client";

import { ChartWrapper } from "charts/src/components/chart";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import React, { useEffect } from "react";
import { z } from "zod";

import { ErrorView } from "@/components/error-view";
import { useChartConfigDetails } from "@/features/analysis/services/get-chart-config-details";
import { timeFrameSchema } from "@/features/analysis/services/get-chart-config-list";
import { useDialog } from "@/hooks/use-dialog";
import { useInView } from "@/hooks/use-in-view";
import { useUpdateQuery } from "@/hooks/use-update-query";
import { Lang } from "@/i18n-config";
import { formatSymbol } from "@/utils/i18n/format-symbol";
import { getTranslations } from "@/utils/i18n/get-translations";

import { LoadingIndicator } from "ui/core/button/LoadingIndicator";

import { apiDataToInstanceData } from "../data-convert/api-data-to-instance-data";
import { apiDataToInstanceDataConfig } from "../data-convert/api-data-to-instance-data-config";
import { apiDataToInstanceLabels } from "../data-convert/api-data-to-instance-labels";
import { apiDataToWatermarkPosition } from "../data-convert/api-data-to-watermark-position";
import {
  DUMMY_RANGE,
  DUMMY_VALUE_FORMAT_TYPE,
  WATERMARK_SIZE,
  getChartUiConfig,
} from "../data/chart-configs";
import { isChartSupportGivenTimeFrame } from "../utils";
import { ChartDialog } from "./ChartDialog";

type ChartGridItemProps = {
  symbol: string;
  lang: Lang;
  chartId: string;
  timeFrameOption: z.infer<typeof timeFrameSchema>;
  timeFrame: z.infer<typeof timeFrameSchema>[number];
  limit?: number;
  renderCallback?: () => void;
};

const DIALOG_WIDTH = 800;

export const ChartGridItem = ({
  lang,
  symbol,
  chartId,
  timeFrame,
  timeFrameOption,
  renderCallback,
  limit = 20,
}: ChartGridItemProps) => {
  const isSupportTimeframe = isChartSupportGivenTimeFrame(
    timeFrameOption,
    timeFrame
  );

  const { ref, isInView } = useInView({ shouldeDetect: isSupportTimeframe });
  const {
    Dialog,
    isOpen: isDialogOpen,
    openDialog,
  } = useDialog({
    isMobileFullScreen: false,
  });
  const searchParams = useSearchParams();
  const { updateQuery } = useUpdateQuery();

  const {
    data: chartConfigDetails,
    isError,
    isFetching,
  } = useChartConfigDetails(
    symbol,
    chartId,
    timeFrame,
    limit,
    isInView && isSupportTimeframe
  );

  const { t } = getTranslations(lang);

  const isDataValueEmpty = chartConfigDetails?.chartDatas.every((chartData) =>
    chartData.data.every((data) => !data.value)
  );

  useEffect(() => {
    updateQuery({ name: "chartId", value: isDialogOpen ? chartId : "" });
  }, [isDialogOpen]);

  useEffect(() => {
    if (searchParams.get("chartId") === chartId) openDialog();
  }, [searchParams, chartId, openDialog]);

  useEffect(() => {
    if (isDataValueEmpty) {
      renderCallback?.();
    }
  }, [isDataValueEmpty]);

  // 需要與 isDataValueEmpty 分開，否則不會有 Loading 畫面
  if (!isSupportTimeframe) {
    return null;
  }

  if (!isInView) {
    return <div ref={ref} />;
  }

  if (isError) {
    return (
      <GridItem
        body={
          <ErrorView children={t.error.somethingWentWrong} errorType="error" />
        }
      />
    );
  }

  if (!chartConfigDetails || isFetching) {
    return (
      <GridItem
        body={
          <div className="flex h-full w-full items-center justify-center">
            <LoadingIndicator />
          </div>
        }
      />
    );
  }

  if (isDataValueEmpty) {
    return null;
  }

  const converterInputParams = {
    chartConfigDetails,
    timeFrame,
    chartId,
    symbol,
    lang,
    mode: "grid" as const,
  };

  const instanceData = apiDataToInstanceData(converterInputParams);
  const instanceDataConfig = apiDataToInstanceDataConfig(converterInputParams);
  const instanceLabels = apiDataToInstanceLabels(converterInputParams);
  const watermarkPosition = apiDataToWatermarkPosition(converterInputParams);

  // 非多數據圖表，仍需第一個圖表的 type
  const firstChartType = chartConfigDetails.chartDatas[0].chartType;
  const { colorSet, iconSrc } = getChartUiConfig(
    chartId,
    instanceDataConfig.length
  );

  const displaySymbol = formatSymbol(symbol);

  return (
    <div ref={ref}>
      <Dialog width={DIALOG_WIDTH}>
        <ChartDialog
          chartId={chartId}
          chartConfigDetails={chartConfigDetails}
          timeFrame={timeFrame}
          lang={lang}
          symbol={symbol}
        />
      </Dialog>
      <GridItem
        openDialog={openDialog}
        title={
          <>
            <Image
              src={iconSrc}
              alt={`${symbol} ${chartConfigDetails.title}`}
              className="relative top-[1px]"
              width={24}
              height={12}
            />
            {`${displaySymbol} ${chartConfigDetails.title}`}
          </>
        }
        body={
          <>
            <Image
              src="/watermark/growin.png"
              className={`absolute z-10 opacity-20`}
              style={{
                left: watermarkPosition.left,
                top: watermarkPosition.top,
              }}
              alt="Growin"
              width={WATERMARK_SIZE}
              height={WATERMARK_SIZE}
            />
            <ChartWrapper
              isControllable={false}
              valueFormatType={DUMMY_VALUE_FORMAT_TYPE}
              className="!bg-[#FAFAFA]"
              chartKey="financial_metrices_basic_chart"
              chartType={firstChartType}
              range={DUMMY_RANGE}
              timeFrame={timeFrame}
              symbols={[symbol]}
              locale={lang}
              instanceData={instanceData}
              instanceLabels={instanceLabels}
              instanceDataConfig={instanceDataConfig}
              customColorset={colorSet}
            />
          </>
        }
      />
    </div>
  );
};

const ChartGridItemWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative w-full rounded-md bg-[#FAFAFA] shadow-md">
      {children}
    </div>
  );
};

const ChartGridItemBody = ({ children }: { children: React.ReactNode }) => {
  return <div className="relative h-[280px] sm:h-[320px]">{children}</div>;
};

const ChartGridItemTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <h3 className="relative z-10 mb-[-24px] flex items-center gap-2 pl-4 pt-6 font-semibold text-gray-700">
      {children}
    </h3>
  );
};

const GridItem = ({
  title = "",
  openDialog,
  body,
}: {
  title?: React.ReactNode;
  openDialog?: () => void;
  body: React.ReactNode;
}) => {
  return (
    <ChartGridItemWrapper>
      <div
        className="absolute right-4 top-[28px] z-50 hidden cursor-pointer sm:block"
        onClick={openDialog}
      >
        <Image src="/icons/expand.svg" width={16} height={16} alt="expand" />
      </div>
      <ChartGridItemTitle>{title}</ChartGridItemTitle>
      <ChartGridItemBody>{body}</ChartGridItemBody>
    </ChartGridItemWrapper>
  );
};
